import { PeriodWithStartAndEnd } from '../../../domain/financial-statement/utils/common/utilityClasses';
import { InvalidParameterException } from '../../error/typed-error';
import { LocalDate, LocalMonthDate, LocalQuarterDate } from '../../../core/pojo/date-variable';
import { LocalDateTime } from "aws-sdk/clients/forecastservice";

export class DateService {

  /**
   * Gets two dates and returns the number of days between them
   */
  static getDaysBetween(date1: Date, date2: Date): number {
    return Math.abs(Math.floor((new Date(date1).getTime() - new Date(date2).getTime()) / (1000 * 3600 * 24)));
  }

  /**
   * Creates a new Date object in UTC from a string representation of a date.
   * The timezone offset is adjusted to match UTC.
   * Note: This function is useful when interacting with a backend that uses UTC.
   * Your date **always** automatically convert to UTC when you send it to backend.
   * So, if you want to save the original date on backend you should reverse your
   * date before sending it. Example:
   * I am in UTC+2 timezone and I want to save on backend 2023-06-30 00:00:00.000.
   * If I send it without any preparation, I get 2023-06-29 22:00:00.000 on backend.
   * To avoid it I add these two hours in advance with this function and send
   * to backend 2023-06-30 02:00:00.000. Finally, I will get the original
   * 2023-06-30 00:00:00.000 date on backend! 🎉
   * If you want just to convert your date to UTC, you just add or substract
   * the appropriate number of hours:
   * I am in UTC+2 timezone, I have the time 2023-06-30 00:00:00.000 and I need to
   * to convert it to UTC. So I substract these two hours and that's all. Use
   * this function with willBeSendToBack inag to false state.
   *
   * @param stringDate - The string representation of the date.
   * @param willBeSendToBack - The flag that indicates whether you want to send
   * the original stringDate date to backend.
   *
   * @returns A new Date object representing the date in UTC or in inverse UTC (for sending to back).
   */
  static createUTCDate(stringDate: string, willBeSendToBack: boolean = true): Date {
    if (!stringDate) {
      throw new InvalidParameterException('createUTCDate() | Parameter was not provided!');
    }
    // Create a new Date object in the local timezone
    const angularDateGMT = new Date(stringDate);
    // Get the local timezone offset in minutes
    const localTimeOffsetWithUTC = angularDateGMT.getTimezoneOffset();

    const multiplier = willBeSendToBack ? -1 : 1;
    return new Date(angularDateGMT.getTime() + multiplier * localTimeOffsetWithUTC * 60 * 1000);
  }

  /**
   * Parses date from "30.06.2023" to "2023-06-30". <br>
   * Throws an exception in case of invalid param type.
   *
   * @param date is like "xx.xx.xxxx"
   * @return date like "xxxx-xx-xx"
   */
  static dotLocalDateToHyphenDate(date: string): string {
    if (!date || date.length !== 10 || date[2] !== '.') {
      throw new InvalidParameterException('dotLocalDateToHyphenDate() | Parameter is wrong!');
    }

    return DateService.hyphenDate(date.substring(6), date.substring(3, 5), date.substring(0, 2));
  }

  /**
   * Parses date from "30/06/2023" to "2023-06-30". <br>
   * Throws an exception in case of invalid param type.
   *
   * @param date is like "xx/xx/xxxx" or "xx/x/xxxx" or "x/xx/xxxx" or "x/x/xxxx"
   * @return date like "xxxx-xx-xx"
   */
  static slashesLocalDateToHyphenDate(date: string): string {
    if (!date || date.length < 8 || date.length > 10 || (date.match(new RegExp('/', 'g')) || []).length !== 2) {
      throw new InvalidParameterException('slashesLocalDateToHyphenDate() | Parameter is wrong!');
    }
    const segments = date.split('/');
    return this.hyphenDate(segments[2], segments[1], segments[0]);
  }

  /**
   * Parses date from "Tue Jul 11 2023 19:15:20 GMT+0200 to "2023-07-11". <br>
   * Throws an exception in case of invalid param type.
   *
   * @param date is like "Tue Jul 11 2023 19:15:20 GMT+0200"
   * @return date like "xxxx-xx-xx"
   */
  static toHyphenDate(date: Date): string {
    if (!date) {
      throw new InvalidParameterException('toHyphenDate() | Parameter was not provided!');
    }
    return this.hyphenDate(date.getFullYear(), date.getMonth() + 1, date.getUTCDate());
  }

  /**
   * Parses date from "Tue Jul 11 2023 19:15:20 GMT+0200" to "2023-07". <br>
   * Throws an exception in case of invalid param type.
   *
   * @param date is like "Tue Jul 11 2023 19:15:20 GMT+0200"
   * @return date like "xxxx-xx"
   */
  static toHyphenDateYearAndMonth(date: Date): string {
    if (!date) {
      throw new InvalidParameterException('toHyphenDateYearAndMonth() | Parameter was not provided!');
    }
    const fullHyphenDate = this.hyphenDate(date.getFullYear(), date.getMonth() + 1, date.getUTCDate());
    return fullHyphenDate.substring(0, fullHyphenDate.length - 3);
  }

  /**
   * Parses date from "44196" to "2023-07-11". <br>
   * Throws an exception in case of invalid param type.
   *
   * @param date is like "44196"
   * @return date like "xxxx-xx-xx"
   */
  static numberToHyphenDate(date: number): string {
    if (!date) {
      throw new InvalidParameterException('numberToHyphenDate() | Parameter was not provided!');
    }
    return this.toHyphenDate(new Date(Date.UTC(0, 0, date - 1)));
  }

  /**
   * Converts a date string to a standardized ISO 8601 date string (YYYY-MM-DD).
   *
   * This function takes a date string in various possible formats and converts it into
   * a standardized ISO 8601 date format. This conversion ignores the time portion and
   * focuses only on the date. The function handles different date string inputs by
   * utilizing the JavaScript Date object's parsing capabilities.
   *
   * Throws an exception in case of invalid param type.
   *
   * @param dateString - The date string to be converted.
   * @returns A string representing the date in ISO 8601 format (YYYY-MM-DD).
   *
   * Usage example:
   * const isoDate = convertDateToISO("Sun Dec 10 2023 02:00:00 GMT+0200");
   * console.log(isoDate); // Outputs: "2023-12-10"
   */
  static convertDateToISO(dateString: string): string {
    if (!dateString) {
      throw new InvalidParameterException('convertDateToISO() | Parameter was not provided!');
    }

    const date = new Date(dateString);
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  /**
   * Compares two date strings and determines if the first date is earlier, later, or the same as the second date.
   *
   * This function compares two date strings by first converting them into a standardized
   * ISO 8601 date format (YYYY-MM-DD) using the `convertDateToISO` function. It then
   * compares these dates to determine if the first date is earlier, later, or the same
   * date as the second date. The function is useful for comparing dates in a uniform manner,
   * irrespective of the input format or the presence of time information in the input strings.
   *
   * Throws an exception in case of invalid param type.
   *
   * @param dateString1 - The first date string to be compared.
   * @param dateString2 - The second date string to be compared.
   * @returns Returns 1 if the first date is later, -1 if earlier, or 0 if the dates are the same.
   *
   * Usage example:
   * const comparisonResult = compareTwoDates("2023-12-10", "2023-12-08");
   * console.log(comparisonResult); // Outputs: 1 (since "2023-12-10" is later than "2023-12-08")
   */
  static compareTwoDates(dateString1: string, dateString2: string): number {
    if (!dateString1 || !dateString2) {
      throw new InvalidParameterException('convertDateToISO() | At least one parameters  was not provided!');
    }
    const isoDate1 = this.convertDateToISO(dateString1);
    const isoDate2 = this.convertDateToISO(dateString2);
    return isoDate1.localeCompare(isoDate2);
  }

  /**
   * Provides the quarter of the provided year:
   * E.x.:
   * 2021-02-01 -> 1
   * 2021-03-01 -> 1
   * 2021-04-01 -> 2
   *
   * Throws an exception in case of invalid param type.
   *
   * @param date must be defined.
   */
  static getQuarter(date: Date): number {
    if (!date) {
      throw new InvalidParameterException('getQuarter() | Parameter was not provided!');
    }
    const month = date.getMonth();
    return Math.floor(month / 3) + 1;
  }

  /**
   * Provides the quarter of the provided year:
   * E.x.:
   * 2021-Q1 -> 1
   * 2021-Q2 -> 2
   *
   * Throws an exception in case of invalid param type.
   *
   * @param date must be defined.
   */
  static getQuarterFromQuarterFormat(date: LocalQuarterDate): number {
    if (!date) {
      throw new InvalidParameterException('getQuarter() | Parameter was not provided!');
    }

    const match = date.match(/^(\d{4})-Q([1-4])$/);
    if (!match) {
      throw new InvalidParameterException('getQuarter() | Invalid date format!');
    }

    return parseInt(match[2], 10);
  }

  /**
   * Provides the year from the provided quarter format:
   * E.x.:
   * 2021-Q1 -> 2021
   * 2023-Q2 -> 2023
   *
   * Throws an exception in case of invalid param type.
   *
   * @param date must be defined.
   */
  static getYearFromQuarterFormat(date: LocalQuarterDate): number {
    if (!date) {
      throw new InvalidParameterException('getYearFromQuarterFormat() | Parameter was not provided!');
    }

    const match = date.match(/^(\d{4})-Q([1-4])$/);
    if (!match) {
      throw new InvalidParameterException('getYearFromQuarterFormat() | Invalid date format!');
    }

    return parseInt(match[1], 10);
  }

  /**
   * Provides the start date of the provided quarter of the given year.
   * E.x.:
   * 2021-Q1 -> 2021-01-01
   * 2023-Q2 -> 2023-04-01
   *
   * Throws an exception in case of invalid param type.
   *
   * @param date must be defined in the format "YYYY-QX".
   */
  static getQuarterStartDate(date: LocalQuarterDate): Date {
    if (!date) {
      throw new InvalidParameterException('getQuarterStartDate() | Parameter was not provided!');
    }

    const match = date.match(/^(\d{4})-Q([1-4])$/);
    if (!match) {
      throw new InvalidParameterException('getQuarterStartDate() | Invalid date format!');
    }

    const year = parseInt(match[1], 10);
    const quarter = parseInt(match[2], 10);
    const month = (quarter - 1) * 3;

    return new Date(year, month, 1);
  }


  /**
   * Provides the utc date of the last day in the quarter. Example:
   * 2024-03-31T00:00:00.000Z
   *
   * Throws an exception in case of invalid param type.
   *
   * @param quarter is between 1 and 4.
   * @param year like 2024.
   */
  static getUTCDateForLastDay(quarter: QuarterInYear, year: number): Date {
    if (!year) {
      throw new InvalidParameterException('getUTCDateForLastDay(quarter: QuarterInYear, year: number) | year must be provided');
    }
    if (!quarter) {
      throw new InvalidParameterException('getUTCDateForLastDay(quarter: QuarterInYear, year: number) | quarter must be provided');
    }
    if (quarter < 1 || quarter > 4) {
      throw new InvalidParameterException(
        'getUTCDateForLastDay(quarter: QuarterInYear, year: number) | Quarter must be between 1 and 4. Current value: ' + quarter);
    }
    const quarterEndings = [[3, 31], [6, 30], [9, 30], [12, 31]];
    const paramDate = new Date();
    paramDate.setUTCFullYear(year, (quarter * 3) - 1, 1);

    return quarterEndings
      .map((dates) => {
        const date = new Date();
        date.setUTCFullYear(year, dates[0] - 1, dates[1]);
        date.setUTCHours(0, 0, 0, 0);
        return new Date(date.toISOString());
      })
      .filter((lastQuarterDay) => lastQuarterDay >= paramDate)[0];
  }

  /**
   * Provides the utc date of the first day in the quarter. Example:
   * 2024-03-01T00:00:00.000Z
   *
   * Throws an exception in case of invalid param type.
   *
   * @param quarter is between 1 and 4.
   * @param year like 2024.
   */
  static getUTCDateForFirstDay(quarter: QuarterInYear, year: number): Date {
    if (!year) {
      throw new InvalidParameterException('getUTCDateForFirstDay(quarter: QuarterInYear, year: number) | year must be provided');
    }
    if (!quarter) {
      throw new InvalidParameterException('getUTCDateForFirstDay(quarter: QuarterInYear, year: number) | quarter must be provided');
    }
    if (quarter < 1 || quarter > 4) {
      throw new InvalidParameterException(
        'getUTCDateForFirstDay(quarter: QuarterInYear, year: number) | Quarter must be between 1 and 4. Current value: ' + quarter);
    }
    const paramDate = new Date();
    paramDate.setUTCFullYear(year, (quarter * 3) - 1, 1);
    paramDate.setUTCHours(0, 0, 0, 0);
    return new Date(paramDate.toISOString());
  }

  /**
   * Provides a date with a type "2022-06-31"
   *
   * Throws an exception in case of invalid param type.
   *
   * @param year
   * @param month
   * @param day
   */
  static hyphenDate(year: string | number, month: string | number, day: string | number): string {
    if (!year || !month || !day) {
      throw new InvalidParameterException('hyphenDate() | all params must be provided');
    }
    const parsedYear = String(year);

    const monthNumber = Number(month);
    const parsedMonth = monthNumber < 10 ? '0' + monthNumber : monthNumber;

    const dayNumber = Number(day);
    const parsedDay = dayNumber < 10 ? '0' + dayNumber : dayNumber;

    return `${parsedYear}-${parsedMonth}-${parsedDay}`;
  }

  /**
   * Returns the year
   * Example:
   * 1) '2023-01-24' -> '2023'
   * 2) '2024-12-14' -> '2024'
   *
   * @param period - The period  in the format "YYYY-MM-DD".
   * @throws {InvalidParameterException} - Throws an exception if the period parameter is not provided.
   * @return - Returns the year in the format "YYYY".
   */
  static getYear(period: LocalDate): string {
    if (!period) {
      throw new InvalidParameterException('getYear() | all params must be provided');
    }
    return period.slice(0, 4);
  }

  /**
   * Returns the month without the day.
   * Example:
   * 1) '2023-01-24' -> '2023-01'
   * 2) '2023-12-14' -> '2023-12'
   *
   * @param period - The period  in the format "YYYY-MM-DD".
   * @throws {InvalidParameterException} - Throws an exception if the period parameter is not provided.
   * @return - Returns the month without day in the format "YYYY-MM".
   */
  static getMonthWithoutDay(period: LocalDate): string {
    if (!period) {
      throw new InvalidParameterException('getMonthWithoutDay() | all params must be provided');
    }
    return period.slice(0, 7);
  }

  /**
   * Returns the next month without the day.
   * Example:
   * 1) '2023-01-01' -> '2023-02'
   * 2) '2023-12-01' -> '2024-01'
   *
   * @param period - The period  in the format "YYYY-MM-DD".
   * @throws {InvalidParameterException} - Throws an exception if the period parameter is not provided.
   * @return - Returns the next month without day in the format "YYYY-MM".
   */
  static getNextMonthWithoutDay(period: LocalDate): string {
    if (!period) {
      throw new InvalidParameterException('getNextMonthWithoutDay() | all params must be provided');
    }
    let [year, month] = period.split('-').map(Number);
    if (month === 12) {
      month = 0;
      year += 1;
    }
    const formattedMonth = (month + 1).toString().padStart(2, '0');
    return `${year}-${formattedMonth}`;
  }

  /**
   * Returns a date from a previous year based on the selected year and a given number of years to roll back.
   *
   * This function calculates the corresponding date in a previous year by subtracting `yearsAgo` from
   * the year in the given `selectedPeriod`. The resulting date will maintain the same month and day.
   *
   * Example usage:
   * 1) '2023-12-04' and `yearsAgo = 1`  =>  '2022-12-04'.
   * 2) '2023-12-04' and `yearsAgo = 3`  =>  '2020-12-04'.
   *
   * Special Case:
   * - In leap year (VISOKOSNIY GOD), if the input date is February 29th, and the target year is not a leap year,
   *   the function will return February 28th of the target year.
   *
   * @param selectedPeriod - A string representing the date in the format "YYYY-MM-DD".
   * @param yearsAgo - The number of years to roll back from the selected year. Defaults to 1.
   * @returns A string representing the date in the format "YYYY-MM-DD", from `yearsAgo` before the `selectedPeriod`.
   */
  static getPreviousYear(selectedPeriod: LocalDate, yearsAgo = 1): LocalDate {
    if (!selectedPeriod) {
      throw new InvalidParameterException('getPreviousYear() | selectedPeriod must be provided!');
    }

    const [year, month, day] = selectedPeriod.split('-').map(Number);
    const targetYear = year - yearsAgo;

    // Check if it's February 29th and adjust for non-leap year
    if (month === 2 && day === 29 && !DateService.isLeapYear(targetYear)) {
      return `${targetYear}-02-28`;
    }
    const date = new Date(targetYear, month - 1, day);

    const formattedYear = date.getFullYear();
    const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
    const formattedDay = String(date.getDate()).padStart(2, '0');

    return `${formattedYear}-${formattedMonth}-${formattedDay}`;
  }

  /**
   * Determines if a given year is a leap year.
   *
   * @param year - The year to check.
   * @returns `true` if the year is a leap year, `false` otherwise.
   */
  static isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  /**
   * Returns the last day of the previous completed quarter.
   * Example:
   * 1) '17.11.2024' -> '30.09.2024'
   * 2) '30.09.2024' -> '30.09.2024'
   * 3) '01.01.2025' -> '31.12.2024'
   *
   * @param inputDate - The input date in the format "YYYY-MM-DD".
   * @returns The last day of the previous quarter in the format "YYYY-MM-DD".
   */
  static getLastCompletedQuarterEndDate(inputDate: Date): string {
    if (!inputDate) {
      throw new InvalidParameterException('getLastDayOfPreviousQuarter() | inputDate must be provided!');
    }

    const year = inputDate.getFullYear();
    const month = inputDate.getMonth() + 1; // ts months are 0-based
    const day = inputDate.getDate();

    const quarterEndings = [
      [3, 31], // Q1
      [6, 30], // Q2
      [9, 30], // Q3
      [12, 31], // Q4
    ];

    // early return if the date is already the last day of its quarter
    for (let i = 0; i < quarterEndings.length; i++) {
      const [qMonth, qDay] = quarterEndings[i];
      if (month === qMonth && day === qDay) {
        return DateService.hyphenDate(year, qMonth, qDay);
      }
    }

    // If not, calculate the last day of the previous quarter
    let previousQuarter: number;
    let previousQuarterYear = year;

    if (month >= 1 && month <= 3) {
      previousQuarter = 4;
      previousQuarterYear -= 1;
    } else if (month >= 4 && month <= 6) {
      previousQuarter = 1;
    } else if (month >= 7 && month <= 9) {
      previousQuarter = 2;
    } else {
      previousQuarter = 3;
    }

    const [endMonth, endDay] = quarterEndings[previousQuarter - 1];
    return DateService.hyphenDate(previousQuarterYear, endMonth, endDay);
  }

  /**
   * Transforms a date of type YYYY-MM-DD to YYYY-MM
   * @param date is nullable.
   */
  static toLocalMonthDate(date: LocalDate): LocalMonthDate {
    if (!date) {
      return date;
    }
    return date.substring(0, 7);
  }

  /**
   * Transforms a date of type YYYY-MM to YYYY-MM-01
   * @param date is nullable.
   */
  static toLocalDate(date: LocalMonthDate): LocalDate {
    if (!date) {
      return date;
    }
    return date + '-01';
  }

  /**
   * From time of event get string representation of how many days ago it was
   * <p>
   * If passed today's date converts it to "Today", otherwise it starts from "1d ago"
   * <p>
   * If passed time from future, returns absolute difference
   *
   * @param time to convert
   */
  static convertLocalDateTimeToDaysDiff(time: LocalDateTime): string {
    const now = Date.now();
    const then = Date.parse(time);
    // find amount of days in milliseconds difference
    const distance = Math.abs(then - now) / (1000 * 3600 * 24);
    const rounded = Math.round(distance);

    if (rounded < 1) {
      return new Date(now).getDate() === new Date(then).getDate() ? 'Today' : '1d ago';
    }

    return `${rounded}d ago`;
  }

  /**
   * Formats time in Java ISO notation
   * to format `dd-MM-YYYY hh-mm`
   *
   * @param time in ISO format
   */
  static formatLocalDateTime(time: LocalDateTime): string {
    const padTwoDigits = (num: number): string =>
      num.toString().padStart(2, "0");

    const parsed = Date.parse(time);
    const date = new Date(parsed);

    return `${padTwoDigits(date.getDate())}-${padTwoDigits(date.getMonth() + 1)}-${date.getFullYear()}` +
      ` ${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`;
  }

  /**
   * Format a date to `MMM, yyyy` format from any parsable input.
   * Example:
   * ```
   * 2025-05-01 -> May, 2025
   * ```
   *
   * @param period to format
   */
  static formatShortDate(period: string): string {
    return new Date(period).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * Return DateFormat from string
   *
   * @param value string that specify format
   * @return DateFormat type
   */
  static transformDateFormat(value: string): DateFormat {
    switch (value) {
      case 'DD-MM-YYYY':
        return DateFormat['DD-MM-YYYY'];
      case 'YYYY-MM-DD':
        return DateFormat['YYYY-MM-DD'];
      default:
        console.warn(`Invalid date format: ${value}. Defaulting to 'YYYY-MM-DD'.`);
        return DateFormat['YYYY-MM-DD'];
    }
  }
}

export function transformDateFormat(value: string): DateFormat {
  return DateService.transformDateFormat(value);
}

/**
 * Accepts PeriodWithStartAndEnd and returns string format as:
 * 1 Jul - 30 Sep
 *  2020
 */
export const formatPeriod = (period: PeriodWithStartAndEnd): string => {
  const startDate = new Date(period.start);
  const endDate = new Date(period.end);

  const startDay = startDate.getDate();
  const startMonth = startDate.toLocaleString('default', { month: 'short' });
  const endDay = endDate.getDate();
  const endMonth = endDate.toLocaleString('default', { month: 'short' });
  const year = startDate.getFullYear();

  return `${startDay} ${startMonth} — ${endDay} ${endMonth} <br> ${year}`;
};

export const YYYY_MM_DD = 'YYYY-MM-DD';
export const DD_MM_YYYY = 'DD-MM-YYYY';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type QuarterInYear = number; // [1,2,3,4] as const

/**
 * Date format types.
 */
export enum DateFormat {
  'DD-MM-YYYY' = 'DD-MM-YYYY',
  'YYYY-MM-DD' = 'YYYY-MM-DD',
}
